import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useTranslation } from "react-i18next";
import SearchableAutocomplete from "./SearchableAutocomplete";
import {
  cancelSpaAppointmentGroup,
  checkSpaAppointmentAvailability,
  createSpaAppointmentGroup,
  dateTimeWithLocalOffset,
  getSpaAppointmentGroup,
  updateSpaAppointmentGroup,
} from "../APIs/SpaAPI";

const emptyRow = (date, startTime = "09:00") => ({
  id: null,
  clientId: "",
  clientName: "",
  treatmentId: "",
  date,
  startTime,
  endTime: "",
  customerType: "EXTERNAL_GUEST",
  commercialStatus: "OPTION",
  operatorIds: [],
  cabinIds: [],
});

const personLabel = (person = {}) => {
  if (typeof person === "string") return person;
  return person.displayName
    || [person.name, person.surname].filter(Boolean).join(" ").trim()
    || person.email
    || (person.id ? `#${person.id}` : "");
};

const parseManualClient = (value) => {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return { name: parts[0], surname: parts.slice(1).join(" ") };
};

const endTimeFor = (row, treatments, interval) => {
  if (row.endTime) return row.endTime;
  const treatment = treatments.find((item) => Number(item.id) === Number(row.treatmentId));
  const duration = Math.max(Number(treatment?.durationMinutes || interval || 15), 1);
  const [hours, minutes] = String(row.startTime || "00:00").split(":").map(Number);
  const total = (hours * 60) + minutes + duration;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const timeToMinutes = (value) => {
  const [hours, minutes] = String(value || "00:00").split(":").map(Number);
  return (hours * 60) + minutes;
};

const addOneDay = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const readableErrorValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value?.message === "string") return value.message;
  if (typeof value?.error === "string") return value.error;
  try {
    return JSON.stringify(value);
  } catch (_) {
    return "Unable to save booking group";
  }
};

const formatRejection = (rejection = {}) => {
  const scope = String(rejection.scope || "CONFLICT").replaceAll("_", " ");
  const resourceIds = Array.isArray(rejection.resourceIds) && rejection.resourceIds.length
    ? ` (${rejection.resourceIds.join(", ")})`
    : "";
  const conflicts = Array.isArray(rejection.conflicts)
    ? rejection.conflicts.map((conflict) => (
      readableErrorValue(conflict?.message || conflict?.code || conflict)
    )).filter(Boolean)
    : [];
  const detail = conflicts.join(", ") || readableErrorValue(rejection.message || rejection.code);
  return `${scope}${resourceIds}${detail ? `: ${detail}` : ""}`;
};

const requestErrorMessage = (requestError) => {
  const rejections = Array.isArray(requestError?.rejections) ? requestError.rejections : [];
  if (rejections.length) return rejections.map(formatRejection).filter(Boolean).join("\n");
  return readableErrorValue(requestError?.payload?.error || requestError?.payload?.message)
    || readableErrorValue(requestError?.message)
    || "Unable to save booking group";
};

const appointmentToRow = (appointment) => ({
  id: appointment.id,
  clientId: appointment.clientId ? String(appointment.clientId) : "",
  clientName: appointment.clientName || "",
  treatmentId: appointment.treatmentId ? String(appointment.treatmentId) : "",
  date: appointment.date,
  startTime: appointment.startTime,
  endTime: appointment.endTime,
  customerType: appointment.customerType || "EXTERNAL_GUEST",
  commercialStatus: appointment.commercialStatus || "OPTION",
  operatorIds: (appointment.resourceCombination?.operatorIds || []).map(String),
  cabinIds: (appointment.resourceCombination?.cabinIds || []).map(String),
  equipment: appointment.resourceCombination?.equipment || appointment.equipmentSelections || [],
});

export default function SpaGroupBookingDialog({
  open,
  groupId,
  initialDate,
  clients,
  treatments,
  operators,
  cabins,
  plannerInterval,
  token,
  tenant,
  canManageAppointments = false,
  canManageFinance,
  ensureCabinId,
  onClose,
  onSaved,
  showSnackbar,
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState("CANCELLED");
  const [reason, setReason] = useState("");
  const editing = Boolean(groupId);
  const bookingKey = JSON.stringify([open, groupId, token, tenant, canManageAppointments, rows]);
  const currentBookingKey = useRef(bookingKey);
  currentBookingKey.current = bookingKey;

  useEffect(() => {
    if (!open) return;
    setError("");
    setReason("");
    setOutcome("CANCELLED");
    if (!groupId) {
      setRows([emptyRow(initialDate), emptyRow(initialDate, "10:00")]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSpaAppointmentGroup(groupId, token, tenant)
      .then((group) => {
        if (!cancelled) setRows(group.appointments.map(appointmentToRow));
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError?.message || "Unable to load booking group");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupId, initialDate, open, tenant, token]);

  const clientById = useMemo(
    () => new Map(clients.map((client) => [String(client.id), client])),
    [clients]
  );

  const updateRow = (index, patch) => {
    if (!canManageAppointments || saving) return;
    setRows((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    )));
  };

  const addRow = () => {
    if (!canManageAppointments || saving) return;
    const previous = rows[rows.length - 1];
    setRows((current) => [...current, emptyRow(previous?.date || initialDate, previous?.startTime || "09:00")]);
  };

  const removeRow = (index) => {
    if (!canManageAppointments || saving) return;
    if (rows.length <= 2) return;
    setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
  };

  const validate = () => {
    if (rows.length < 2) return "A group booking requires at least two appointments.";
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      if (!row.clientId && !parseManualClient(row.clientName)) return `Guest ${index + 1}: select a client or enter name and surname.`;
      if (!row.treatmentId) return `Guest ${index + 1}: select a treatment.`;
      if (!row.date || !row.startTime) return `Guest ${index + 1}: select date and start time.`;
      if (!row.operatorIds.length) return `Guest ${index + 1}: select at least one operator.`;
    }
    const ranges = rows.map((row) => {
      const start = new Date(`${row.date}T${row.startTime}:00`);
      const endTime = endTimeFor(row, treatments, plannerInterval);
      const endDate = timeToMinutes(endTime) <= timeToMinutes(row.startTime) ? addOneDay(row.date) : row.date;
      return { start, end: new Date(`${endDate}T${endTime}:00`) };
    });
    for (let left = 0; left < rows.length; left += 1) {
      for (let right = left + 1; right < rows.length; right += 1) {
        const overlaps = ranges[left].start < ranges[right].end && ranges[right].start < ranges[left].end;
        if (!overlaps) continue;
        const sharedOperatorId = rows[left].operatorIds.find((id) => rows[right].operatorIds.includes(id));
        if (sharedOperatorId) {
          const operatorName = personLabel(operators.find((item) => String(item.id) === String(sharedOperatorId)) || {}) || `#${sharedOperatorId}`;
          return `Guests ${left + 1} and ${right + 1} overlap for operator ${operatorName}.`;
        }
      }
    }
    for (const cabin of cabins) {
      const cabinRows = rows.flatMap((row, index) => (
        row.cabinIds.some((id) => String(id) === String(cabin.id))
          ? [{ ...ranges[index], guest: index + 1 }]
          : []
      ));
      const events = cabinRows.flatMap(({ start, end }) => ([
        { time: start.getTime(), change: 1 },
        { time: end.getTime(), change: -1 },
      ])).sort((left, right) => left.time - right.time || left.change - right.change);
      let occupied = 0;
      for (const event of events) {
        occupied += event.change;
        if (occupied > Number(cabin.capacity || 1)) {
          return `${cabin.name}: ${occupied} guests exceed cabin capacity ${Number(cabin.capacity || 1)}.`;
        }
      }
    }
    return "";
  };

  const createPayload = async () => ({
    appointments: await Promise.all(rows.map(async (row) => {
      const resolvedCabinIds = (await Promise.all(row.cabinIds.map((id) => ensureCabinId(id)))).filter(Boolean);
      const clientId = Number(row.clientId);
      const preferredResources = { operatorIds: row.operatorIds.map(Number), cabinIds: resolvedCabinIds.map(Number), equipmentIds: [] };
      const availability = await checkSpaAppointmentAvailability({ treatmentProductId: Number(row.treatmentId), addOns: [],
        startAt: dateTimeWithLocalOffset(row.date, row.startTime), partySize: 1, preferredResources }, token, tenant);
      const combination = availability?.available && availability.combinations?.find((item) =>
        preferredResources.operatorIds.every((id) => item.operatorIds?.includes(id))
        && preferredResources.cabinIds.every((id) => item.cabinIds?.includes(id)));
      if (!combination) {
        const failure = new Error('The selected group resources are unavailable.');
        failure.rejections = availability?.rejections || [];
        throw failure;
      }
      return {
        ...(clientId ? { clientId } : { client: parseManualClient(row.clientName) }),
        treatmentProductId: Number(row.treatmentId),
        startAt: dateTimeWithLocalOffset(row.date, row.startTime),
        partySize: 1,
        customerType: row.customerType,
        commercialStatus: row.commercialStatus,
        participants: [],
        preferredResources,
        resources: combination,
        pmsReference: {},
      };
    })),
  });

  const updatePayload = async () => ({
    appointments: await Promise.all(rows.map(async (row) => {
      const resolvedCabinIds = (await Promise.all(row.cabinIds.map((id) => ensureCabinId(id)))).filter(Boolean);
      const endTime = endTimeFor(row, treatments, plannerInterval);
      const endDate = timeToMinutes(endTime) <= timeToMinutes(row.startTime) ? addOneDay(row.date) : row.date;
      return {
        id: Number(row.id),
        startAt: dateTimeWithLocalOffset(row.date, row.startTime),
        endAt: dateTimeWithLocalOffset(endDate, endTime),
        preferredResources: {
          operatorIds: row.operatorIds.map(Number),
          cabinIds: resolvedCabinIds.map(Number),
          equipmentIds: (row.equipment || []).map((item) => item.equipmentId),
        },
        resources: {
          operatorIds: row.operatorIds.map(Number),
          cabinIds: resolvedCabinIds.map(Number),
          equipment: row.equipment || [],
        },
      };
    })),
  });

  const save = async () => {
    if (!canManageAppointments || saving || loading) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = editing ? await updatePayload() : await createPayload();
      if (bookingKey !== currentBookingKey.current) throw new Error('Booking details or permissions changed. Check the group again.');
      const result = editing
        ? await updateSpaAppointmentGroup(groupId, payload, token, tenant)
        : await createSpaAppointmentGroup(payload, token, tenant);
      showSnackbar(editing ? "Group booking updated" : "Group booking created", "success");
      await onSaved(result);
      onClose();
    } catch (requestError) {
      const message = requestErrorMessage(requestError);
      setError(message);
      showSnackbar(message, requestError?.status === 409 ? "warning" : "error");
    } finally {
      setSaving(false);
    }
  };

  const cancelGroup = async () => {
    if (!canManageAppointments || !canManageFinance || saving || loading) return;
    if (!reason.trim()) {
      setError("A reason is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await cancelSpaAppointmentGroup(groupId, { status: outcome, reason: reason.trim(), appointments: [] }, token, tenant);
      showSnackbar(outcome === "NO_SHOW" ? "Group marked as no-show" : "Group booking cancelled", "success");
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <GroupsOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={900}>{editing ? `Group booking #${groupId}` : "New couple / group booking"}</Typography>
            <Typography variant="caption" color="text.secondary">Each row creates one linked appointment.</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {error ? <Alert severity="warning" sx={{ whiteSpace: "pre-line" }}>{error}</Alert> : null}
          {loading ? <Typography color="text.secondary">Loading group...</Typography> : rows.map((row, index) => (
            <Paper key={row.id || `new-${index}`} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" color="primary" label={index + 1} />
                  <Typography fontWeight={850}>{row.clientName || `Guest ${index + 1}`}</Typography>
                </Stack>
                {!editing ? (
                  <IconButton aria-label={`Remove guest ${index + 1}`} color="error" disabled={!canManageAppointments || saving || rows.length <= 2} onClick={() => removeRow(index)}>
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                ) : null}
              </Stack>
              <Grid container spacing={1.25}>
                <Grid item xs={12} md={4}>
                  <SearchableAutocomplete
                    freeSolo
                    disabled={editing || !canManageAppointments || saving}
                    options={clients}
                    value={clientById.get(String(row.clientId)) || null}
                    inputValue={row.clientName}
                    getOptionLabel={personLabel}
                    onChange={(_, client) => updateRow(index, {
                      clientId: client?.id ? String(client.id) : "",
                      clientName: client ? personLabel(client) : row.clientName,
                    })}
                    onInputChange={(_, value, reasonValue) => {
                      if (reasonValue !== "reset") updateRow(index, { clientId: "", clientName: value });
                    }}
                    renderInput={(params) => <TextField {...params} size="small" label="Guest" required />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" required disabled={editing || !canManageAppointments || saving}>
                    <InputLabel id={`group-treatment-${index}`}>Treatment</InputLabel>
                    <Select labelId={`group-treatment-${index}`} label="Treatment" value={row.treatmentId} onChange={(event) => updateRow(index, { treatmentId: event.target.value, endTime: "" })}>
                      {treatments.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small" disabled={editing || !canManageAppointments || saving}>
                    <InputLabel>Guest type</InputLabel>
                    <Select label="Guest type" value={row.customerType} onChange={(event) => updateRow(index, { customerType: event.target.value })}>
                      <MenuItem value="HOTEL_GUEST">Hotel guest</MenuItem>
                      <MenuItem value="EXTERNAL_GUEST">External guest</MenuItem>
                      <MenuItem value="MEMBER">Member</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small" disabled={editing || !canManageAppointments || saving}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={row.commercialStatus} onChange={(event) => updateRow(index, { commercialStatus: event.target.value })}>
                      <MenuItem value="OPTION">Option</MenuItem>
                      <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                      <MenuItem value="GUARANTEED">Guaranteed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" type="date" label="Date" disabled={!canManageAppointments || saving} value={row.date} onChange={(event) => updateRow(index, { date: event.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" type="time" label="Start" disabled={!canManageAppointments || saving} value={row.startTime} onChange={(event) => updateRow(index, { startTime: event.target.value, endTime: "" })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" required disabled={!canManageAppointments || saving}>
                    <InputLabel id={`group-operators-${index}`}>Operators</InputLabel>
                    <Select multiple labelId={`group-operators-${index}`} label="Operators" value={row.operatorIds} onChange={(event) => updateRow(index, { operatorIds: event.target.value })} renderValue={(values) => values.map((id) => personLabel(operators.find((item) => String(item.id) === String(id)))).join(", ")}>
                      {operators.filter((item) => item.active !== false).map((item) => <MenuItem key={item.id} value={String(item.id)}>{personLabel(item)}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" disabled={!canManageAppointments || saving}>
                    <InputLabel id={`group-cabins-${index}`}>Cabins</InputLabel>
                    <Select multiple labelId={`group-cabins-${index}`} label="Cabins" value={row.cabinIds} onChange={(event) => updateRow(index, { cabinIds: event.target.value })} renderValue={(values) => values.map((id) => cabins.find((item) => String(item.id) === String(id))?.name || id).join(", ")}>
                      {cabins.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          ))}
          {!editing && !loading ? (
            <Button startIcon={<AddIcon />} variant="outlined" disabled={!canManageAppointments || saving} onClick={addRow}>Add guest / treatment</Button>
          ) : null}
          {editing && canManageAppointments && canManageFinance && !loading ? (
            <Paper variant="outlined" sx={{ p: 1.5, borderColor: "error.main" }}>
              <Typography fontWeight={850} mb={1}>Cancel or mark the entire group as no-show</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Outcome</InputLabel>
                    <Select label="Outcome" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                      <MenuItem value="NO_SHOW">No-show</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={7}><TextField fullWidth size="small" label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><Button fullWidth color="error" variant="outlined" disabled={saving || !reason.trim()} onClick={cancelGroup}>Apply</Button></Grid>
              </Grid>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t("spa.common.cancel", { defaultValue: "Cancel" })}</Button>
        <Button variant="contained" onClick={save} disabled={!canManageAppointments || saving || loading}>{saving ? "Saving..." : editing ? "Save group schedule" : "Create group"}</Button>
      </DialogActions>
    </Dialog>
  );
}
